import Grid from '@mui/material/Grid';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as UscgSafetyIcon } from '../../../assets/images/uscg-safety-wrapper.svg';
import TitleGrid from '../../../components/grid/TitleGrid';
import BreadCrumb from '../../../components/title-page/BreadCrumb';
import TitlePage from '../../../components/title-page/TitlePage';
import { NotificationContext } from '../../../context/notificationContext';
import { UscgSafetyContext } from '../../../context/uscgSafetyContext';
import { VesselContext } from '../../../context/vesselContext';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { defaultPagination } from '../../../utils/constants/defaults';
import { Pagination } from '../../../utils/types/pagination';
import TemplateBox from '../../form-templates/TemplateBox';
import VesselEmptyListService from '../VesselEmptyListService';
import getUscgSafetyGridColumns from './ListGridColumns';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import MrtDataTable from '../../../components/mrt-data-table';
import { UscgSafetyInterface } from '../../../utils/types/uscgSafety';
import { useNavigate } from 'react-router-dom';
import paths from '../../../utils/constants/paths';
import AlertDialog from '../../../components/dialog/AlertDialog';
import { useLocation } from 'react-router-dom';

const defaultAttributesParams: string[] = [];
const defaultOrderParams: string[] = ['updatedAt:DESC'];
const defaultPaginationParams = {
  ...defaultPagination,
  attributes: defaultAttributesParams,
  join: ['creator', 'operator'],
  order: defaultOrderParams
};

const UscgSafetyList: FC = () => {
  const query = useQueryParams();
  const navigate = useNavigate();

  const { viewVessel, state: vesselState } = useContext(VesselContext);
  const {
    listUscgSafety,
    deleteUscgSafety,
    state: uscgSafetyState
  } = useContext(UscgSafetyContext);
  const { showAlert } = useContext(NotificationContext);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<UscgSafetyInterface | null>(null);

  const [vesselId] = useState<string>(query.get('vesselId') || '');
  const [paginationParams, setPaginationParams] = useState<Pagination>({
    ...defaultPaginationParams,
    filter: `vessel_id:${vesselId}`
  });

  useEffect(() => {
    if (!vesselState.view.data?.id) viewVessel(vesselId);

    return () => {
      document.body.style.backgroundColor = '#FFF';
    };
  }, []);

  useEffect(() => {
    if (uscgSafetyState.delete.done) {
      listUscgSafetyPaginated(paginationParams);

      showAlert({
        variant: 'success',
        title: 'USCG Safety',
        description: 'USCG Safety deleted!'
      });
    } else if (uscgSafetyState.delete.error) {
      showAlert({
        variant: 'error',
        title: 'USCG Safety',
        description: "USCG Safety couldn't be deleted!"
      });
    }
  }, [uscgSafetyState.delete]);

  const onEditUscgSafety = (usgItem: UscgSafetyInterface) => {
    navigate(
      {
        pathname: paths.VESSEL_USCG_SAFETY_FORM.absolutePath,
        search: `id=${usgItem.id}`
      },
      {
        state: { usgItem }
      }
    );
  };

  const handleConfirmDelete = () => {
    if (!selectedRecord) return;

    deleteUscgSafety(selectedRecord.id);
    setOpenDeleteDialog(false);
  };

  const onDeleteUscgSafety = (usgItem: UscgSafetyInterface) => {
    setSelectedRecord(usgItem);
    setOpenDeleteDialog(true);
  };

  const columns = getUscgSafetyGridColumns(onEditUscgSafety, onDeleteUscgSafety);

  useEffect(() => {
    listUscgSafetyPaginated(paginationParams);
  }, [paginationParams]);

  const listUscgSafetyPaginated = useCallback(
    (paginationParams: Pagination) => {
      listUscgSafety(paginationParams);
    },
    [paginationParams]
  );

  const handlePaginationChange = (paginationState: MRT_PaginationState) => {
    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        page: paginationState.pageIndex + 1,
        perPage: paginationState.pageSize
      };
    });
  };

  const handleSortingChange = (sortingState: MRT_SortingState) => {
    const order = sortingState.length
      ? sortingState.map((sort) => `${sort.id}:${sort.desc ? 'DESC' : 'ASC'}`)
      : defaultOrderParams;

    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        order
      };
    });
  };

  return (
    <>
      <TitlePage
        leftContainer={
          <BreadCrumb previous={vesselState.view.data?.name || 'Vessel'} current={'USCG Safety'} />
        }
      />
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        style={{ marginTop: '71px', marginBottom: '49px' }}>
        <TemplateBox height={'calc(100vh - 240px)'}>
          {uscgSafetyState?.list?.data?.results?.length === 0 ? (
            <VesselEmptyListService serviceName={'USCG Safety'} />
          ) : (
            <>
              <TitleGrid icon={<UscgSafetyIcon className="icon-title" />} title={'USCG Safety'} />

              <MrtDataTable
                isLoading={uscgSafetyState.list.loading}
                isError={Boolean(uscgSafetyState.list.error)}
                columns={columns}
                rows={uscgSafetyState?.list?.data?.results}
                rowCount={uscgSafetyState?.list?.data?.pagination?.total}
                initialPagination={{
                  pageIndex: paginationParams.page - 1,
                  pageSize: paginationParams.perPage
                }}
                onPaginationChange={handlePaginationChange}
                onSortingChange={handleSortingChange}
              />
            </>
          )}
        </TemplateBox>
      </Grid>
      {openDeleteDialog && (
        <AlertDialog
          disableButtons={uscgSafetyState.delete.loading}
          content={
            <Grid
              container
              direction="row"
              alignItems="center"
              margin={'auto'}
              justifyContent={'center'}
              textAlign={'center'}>
              <Grid item xs={10} sm={8.7} md={8.7} lg={8.7} xl={8.7}>
                <span style={modalTextStyle}>
                  Are you sure you want to delete this record? This action can not be undone
                </span>
              </Grid>
            </Grid>
          }
          cancelText={'No, cancel'}
          approveText={'Yes, remove'}
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onSave={handleConfirmDelete}
        />
      )}
    </>
  );
};

const modalTextStyle = { color: '#1F1F1F', fontSize: '14px' };

export default UscgSafetyList;
